export interface GridItem {
  i: string
  x: number
  y: number
  w: number
  h: number
}

export interface SizeConstraints {
  minW: number
  minH: number
  maxW?: number
  maxH?: number
}

/** Map of widget id → its size constraints (undefined = no constraints) */
export type ConstraintsById = Record<string, SizeConstraints | undefined>

function projectGridEdge(edge: number, sourceCols: number, targetCols: number): number {
  return Math.round((edge / sourceCols) * targetCols)
}

/** Project position and width independently so equal-sized widgets stay equal at any x. */
export function projectGridItem(item: GridItem, sourceCols: number, targetCols: number): GridItem {
  if (sourceCols === targetCols) return item

  const left = projectGridEdge(item.x, sourceCols, targetCols)
  const w = Math.max(1, Math.min(targetCols, projectGridEdge(item.w, sourceCols, targetCols)))
  // Keep the projected width at the right boundary by shifting the item inward.
  const x = Math.max(0, Math.min(left, targetCols - w))

  return { ...item, x, w }
}

/** Keep shared horizontal edges shared after rounding position-independent widths. */
export function projectGridLayout(
  layout: GridItem[],
  sourceCols: number,
  targetCols: number,
  constraintsById: ConstraintsById = {}
): GridItem[] {
  if (sourceCols === targetCols) return layout
  const projectedById = new Map<string, GridItem>()
  const ordered = [...layout].sort((a, b) => a.x - b.x)
  for (const item of ordered) {
    let projected = projectGridItem(item, sourceCols, targetCols)
    const constraints = constraintsById[item.i]
    if (constraints) projected = applyConstraints(projected, constraints)
    const leftNeighbors = ordered.filter(
      (other) =>
        other.x + other.w === item.x && other.y < item.y + item.h && item.y < other.y + other.h
    )
    if (leftNeighbors.length > 0) {
      const left = Math.max(
        ...leftNeighbors.map((other) => {
          const neighbor = projectedById.get(other.i)!
          return neighbor.x + neighbor.w
        })
      )
      projected = { ...projected, x: Math.min(left, targetCols - projected.w) }
    }
    projectedById.set(item.i, projected)
  }
  return layout.map((item) => projectedById.get(item.i)!)
}

/**
 * Map a layout edited in display-cols space back to the canonical cols it is stored in,
 * so that projecting it again (projectGridLayout + display constraints) reproduces the
 * edit exactly wherever that's reachable.
 *
 * Rounding x and w independently (projectGridItem) is not an inverse of projectGridLayout:
 * it can drop a 1-col resize (the width rounds back to the old canonical width), return a
 * pushed neighbour to where it started, or land a dropped widget one column inside its
 * neighbour, which the commit then resolves by pushing that neighbour down. Instead this
 * inverts projectGridLayout's own rule, left to right: an item renders at its left
 * neighbour's display edge when it touches that neighbour in canonical space, otherwise at
 * its rounded x; its width renders as the rounded width raised to the display minW. Each
 * item takes the canonical x/w that renders to its edited x/w — its previous canonical
 * value when that still does, so untouched widgets don't drift — and never overlaps an
 * item already placed to its left in the same rows. When keeping previous values can't
 * reproduce the edit, a second pass packs every item at its smallest matching width,
 * which can free the canonical column a neighbour needs. An edit no canonical layout can
 * render (some display columns are unreachable) gets the closest one, which
 * refineUnprojectedLayout then tries to improve.
 */
export function unprojectEditedLayout(
  edited: GridItem[],
  prevCanonicalById: Record<string, GridItem | undefined>,
  displayCols: number,
  canonicalCols: number,
  displayConstraintsById: ConstraintsById = {},
  canonicalConstraintsById: ConstraintsById = {}
): GridItem[] {
  if (displayCols === canonicalCols) return edited

  const ratio = displayCols / canonicalCols
  const rowsOverlap = (a: GridItem, b: GridItem) => a.y < b.y + b.h && b.y < a.y + a.h
  const ordered = [...edited].sort((a, b) => (a.x !== b.x ? a.x - b.x : a.y - b.y))

  const construct = (keepPrevious: boolean) => {
    const placed: { canonical: GridItem; display: GridItem }[] = []
    const byId = new Map<string, GridItem>()
    let misses = 0

    for (const item of ordered) {
      const prev = keepPrevious ? prevCanonicalById[item.i] : undefined
      const displayC = displayConstraintsById[item.i]
      const canonicalC = canonicalConstraintsById[item.i]
      const neighbours = placed.filter((p) => rowsOverlap(p.display, item))
      const leftBound = Math.max(0, ...neighbours.map((p) => p.canonical.x + p.canonical.w))

      const renderW = (w: number) => {
        const projected = Math.max(1, Math.min(displayCols, Math.round(w * ratio)))
        return displayC ? applyConstraints({ ...item, w: projected }, displayC).w : projected
      }
      const renderX = (x: number) => {
        const touching = neighbours.filter((p) => p.canonical.x + p.canonical.w === x)
        const left =
          touching.length > 0
            ? Math.max(...touching.map((p) => p.display.x + p.display.w))
            : Math.round(x * ratio)
        return Math.max(0, Math.min(left, displayCols - item.w))
      }
      const pick = (
        range: number[],
        render: (v: number) => number,
        want: number,
        prefer?: number
      ): number => {
        const exact = range.filter((v) => render(v) === want)
        if (prefer !== undefined && exact.includes(prefer)) return prefer
        if (exact.length > 0) return exact[0]
        misses++
        return range.reduce((best, v) =>
          Math.abs(render(v) - want) < Math.abs(render(best) - want) ? v : best
        )
      }

      const minW = Math.max(1, canonicalC?.minW ?? 1)
      const maxW = Math.min(canonicalCols, canonicalC?.maxW ?? canonicalCols)
      const widths: number[] = []
      for (let w = minW; w <= maxW; w++) widths.push(w)
      const w = widths.length > 0 ? pick(widths, renderW, item.w, prev?.w) : minW

      const xs: number[] = []
      for (let x = Math.min(leftBound, canonicalCols - w); x <= canonicalCols - w; x++) xs.push(x)
      const x = xs.length > 0 ? pick(xs, renderX, item.x, prev?.x) : Math.max(0, canonicalCols - w)

      const canonical = { ...item, x, w }
      placed.push({ canonical, display: item })
      byId.set(item.i, canonical)
    }

    return { layout: edited.map((item) => byId.get(item.i)!), misses }
  }

  // Keep untouched widgets' canonical values when that reproduces the edit; if it can't,
  // a tighter packing (smallest widths that still render the same) often frees the
  // column a neighbour needs.
  const stable = construct(true)
  if (stable.misses === 0) return stable.layout
  const packed = construct(false)
  return packed.misses < stable.misses ? packed.layout : stable.layout
}

/**
 * Search near an unprojected layout for one that renders exactly as the user saw it.
 *
 * Display is a rounded projection of canonical cols, so unprojectEditedLayout's local
 * edge choices can still come back different: a 1-col resize that rounds back to the old
 * canonical width (the resize is silently dropped), a pushed neighbour that re-renders
 * where it started, or a slot that fits on screen but is a column narrower in canonical
 * space. Greedily tries small x/w offsets on every item that changed on screen, and ±1
 * x on the untouched ones — a canonical nudge that doesn't move them on screen can open
 * the column a narrow slot needs. Keeps whichever candidate renders closest to `edited`:
 * a changed row costs far more than a changed width, which costs more than a column shift.
 */
export function refineUnprojectedLayout(
  canonical: GridItem[],
  edited: GridItem[],
  prevDisplayById: Record<string, GridItem | undefined>,
  canonicalCols: number,
  reproject: (layout: GridItem[]) => GridItem[]
): GridItem[] {
  const target = new Map(edited.map((item) => [item.i, item]))
  const score = (layout: GridItem[]) =>
    reproject(layout).reduce((sum, item) => {
      const want = target.get(item.i)
      if (!want) return sum
      return (
        sum +
        Math.abs(item.y - want.y) * 100 +
        Math.abs(item.w - want.w) * 10 +
        Math.abs(item.x - want.x)
      )
    }, 0)

  const editedIds = new Set(
    edited
      .filter((item) => {
        const prev = prevDisplayById[item.i]
        return !prev || prev.x !== item.x || prev.y !== item.y || prev.w !== item.w
      })
      .map((item) => item.i)
  )
  const offsetsFor = (id: string): [number, number][] =>
    editedIds.has(id)
      ? [0, -1, 1].flatMap((dw) => [0, -1, 1, -2, 2].map((dx): [number, number] => [dx, dw]))
      : [
          [-1, 0],
          [1, 0],
        ]

  let best = canonical
  let bestScore = score(best)
  for (let pass = 0; pass < 3 && bestScore > 0; pass++) {
    const before = bestScore
    for (const { i: id } of canonical) {
      if (bestScore === 0) break
      const base = best.find((item) => item.i === id)!
      let improved: GridItem[] | null = null
      for (const [dx, dw] of offsetsFor(id)) {
        const w = base.w + dw
        const x = base.x + dx
        if ((dx === 0 && dw === 0) || w < 1 || x < 0 || x + w > canonicalCols) continue
        const candidate = best.map((item) => (item.i === id ? { ...item, x, w } : item))
        const candidateScore = score(candidate)
        if (candidateScore < bestScore) {
          improved = candidate
          bestScore = candidateScore
        }
      }
      if (improved) best = improved
    }
    if (bestScore === before) break
  }
  return best
}

/** Project width-only constraints into the same coordinate space as projected items. */
export function projectSizeConstraints(
  constraints: SizeConstraints,
  sourceCols: number,
  targetCols: number
): SizeConstraints {
  if (sourceCols === targetCols) return constraints

  const projectWidth = (width: number) => Math.max(1, Math.round((width / sourceCols) * targetCols))
  const minW = projectWidth(constraints.minW)
  const maxW =
    constraints.maxW === undefined ? undefined : Math.max(minW, projectWidth(constraints.maxW))

  return { ...constraints, minW, maxW }
}

/** Calculate column width from container dimensions */
export function calcColWidth(
  containerWidth: number,
  cols: number,
  margin: [number, number],
  containerPadding: [number, number]
): number {
  return (containerWidth - 2 * containerPadding[0] - (cols - 1) * margin[0]) / cols
}

/** Convert a grid item to its pixel rect */
export function gridToPxRect(
  item: GridItem,
  colWidth: number,
  rowHeight: number,
  margin: [number, number],
  containerPadding: [number, number]
): { left: number; top: number; width: number; height: number } {
  const left = containerPadding[0] + item.x * (colWidth + margin[0])
  const top = containerPadding[1] + item.y * (rowHeight + margin[1])
  const width = item.w * colWidth + (item.w - 1) * margin[0]
  const height = item.h * rowHeight + (item.h - 1) * margin[1]
  const roundedLeft = Math.round(left)
  const roundedTop = Math.round(top)

  return {
    left: roundedLeft,
    top: roundedTop,
    width: Math.round(left + width) - roundedLeft,
    height: Math.round(top + height) - roundedTop,
  }
}

/** Convert pixel delta to grid unit delta */
export function pxDeltaToGridDelta(
  pxDelta: { x: number; y: number },
  colWidth: number,
  rowHeight: number,
  margin: [number, number]
): { dx: number; dy: number } {
  return {
    dx: Math.round(pxDelta.x / (colWidth + margin[0])),
    dy: Math.round(pxDelta.y / (rowHeight + margin[1])),
  }
}

/**
 * Snap a dragged item to the grid cell under the pointer, preferring a free cell.
 *
 * Plain rounding (pxDeltaToGridDelta) flips to the next column once the pointer drifts
 * half a cell sideways. Dropping into a slot exactly as wide as the widget — the common
 * "put it next to that one" move — then lands one column inside the neighbour, and
 * reflow pushes the neighbour down. So when the rounded cell collides but the cell on the
 * other side of the pointer's fractional position (floor vs ceil, per axis) is free, take
 * the free one. A drop a full cell or more into a widget still collides on every
 * candidate and keeps the rounded cell, so deliberate pushes behave as before.
 */
export function snapDraggedItem(
  startItem: GridItem,
  pxDelta: { x: number; y: number },
  colWidth: number,
  rowHeight: number,
  margin: [number, number],
  layout: GridItem[],
  cols: number,
  rows: number
): GridItem {
  const fx = startItem.x + pxDelta.x / (colWidth + margin[0])
  const fy = startItem.y + pxDelta.y / (rowHeight + margin[1])
  const rounded = clampToGrid({ ...startItem, x: Math.round(fx), y: Math.round(fy) }, cols, rows)
  const others = layout.filter((item) => item.i !== startItem.i)
  const isFree = (candidate: GridItem) => !others.some((o) => isOverlapping(candidate, o))
  if (isFree(rounded)) return rounded

  let best: GridItem | null = null
  let bestDistance = Infinity
  for (const x of [Math.floor(fx), Math.ceil(fx)]) {
    for (const y of [Math.floor(fy), Math.ceil(fy)]) {
      const candidate = clampToGrid({ ...startItem, x, y }, cols, rows)
      if (!isFree(candidate)) continue
      const distance = Math.hypot(candidate.x - fx, candidate.y - fy)
      if (distance < bestDistance) {
        best = candidate
        bestDistance = distance
      }
    }
  }
  return best ?? rounded
}

/** Convert pixel size to grid width/height units */
export function pxSizeToGridSize(
  px: { width: number; height: number },
  colWidth: number,
  rowHeight: number,
  margin: [number, number],
  rounding: { width?: 'floor' | 'ceil' | 'round'; height?: 'floor' | 'ceil' | 'round' } = {}
): { w: number; h: number } {
  const applyRounding = (value: number, mode: 'floor' | 'ceil' | 'round' = 'round') => {
    if (mode === 'floor') return Math.floor(value)
    if (mode === 'ceil') return Math.ceil(value)
    return Math.round(value)
  }

  return {
    w: applyRounding((px.width + margin[0]) / (colWidth + margin[0]), rounding.width),
    h: applyRounding((px.height + margin[1]) / (rowHeight + margin[1]), rounding.height),
  }
}

/** Clamp item so it stays within the grid bounds */
export function clampToGrid(item: GridItem, cols: number, rows: number): GridItem {
  const w = Math.max(1, Math.min(item.w, cols))
  const h = Math.max(1, item.h)
  const x = Math.max(0, Math.min(item.x, cols - w))
  const y = Math.max(0, item.y)
  return { ...item, x, y, w, h }
}

/**
 * Clamp item's x/w so it does not overlap any y-band neighbour.
 * Used during horizontal resize so adjacent widgets are never pushed
 * downward by a width change — the resize is blocked at the boundary.
 */
export function clampItemHorizontal(
  item: GridItem,
  layout: GridItem[],
  direction: 'left' | 'right'
): GridItem {
  let { x, w } = item
  const rightEdge = item.x + item.w

  for (const other of layout) {
    if (other.i === item.i) continue
    if (item.y >= other.y + other.h || item.y + item.h <= other.y) continue

    if (direction === 'right') {
      if (other.x > item.x) w = Math.max(1, Math.min(w, other.x - item.x))
    } else {
      const otherRight = other.x + other.w
      if (otherRight <= rightEdge && x < otherRight) {
        x = otherRight
        w = rightEdge - x
      }
    }
  }

  return { ...item, x: Math.max(0, x), w: Math.max(1, w) }
}

/**
 * Widest a horizontal resize may grow, in grid columns: the smaller of the item's
 * maxW and the distance from the anchored edge to the grid boundary. Shared by the
 * live pixel cap (use-widget-resize) and the committed grid clamp
 * (resolveHorizontalResize) so the drag ghost is never drawn wider than what will
 * actually commit — including for maxW-limited widgets.
 *
 * `growsLeft` = the left edge is being dragged (right edge anchored).
 */
export function maxHorizontalResizeWidth(
  item: GridItem,
  growsLeft: boolean,
  cols: number,
  constraints: SizeConstraints
): number {
  const maxByGrid = growsLeft ? item.x + item.w : cols - item.x
  const bound = constraints.maxW !== undefined ? Math.min(constraints.maxW, maxByGrid) : maxByGrid
  return Math.max(1, bound)
}

/**
 * Resolve a horizontal resize into a grid-bounded { x, w } with the NON-dragged
 * edge anchored.
 *
 * clampToGrid alone is wrong for a resize: when the requested width exceeds the
 * space left in the grid it PRESERVES the width and slides x toward the far edge
 * (`x = min(item.x, cols - w)`) to keep the item in bounds. During a right-edge
 * resize that slides the widget LEFT into the neighbour on the opposite side — a
 * slide that clampItemHorizontal never corrects (it only guards the drag-side
 * neighbour), so the widget commits wider than intended and overlapping its left
 * neighbour, which compaction then resolves by scrambling the whole column.
 *
 * This caps the width at the grid boundary AND the item's min/max width so the
 * anchored edge stays fixed. `growsLeft` = the left edge is being dragged (the
 * right edge is anchored); otherwise the left edge is anchored.
 */
export function resolveHorizontalResize(
  item: GridItem,
  requestedW: number,
  growsLeft: boolean,
  cols: number,
  constraints: SizeConstraints
): { x: number; w: number } {
  const bound = maxHorizontalResizeWidth(item, growsLeft, cols, constraints)
  const w = Math.max(1, Math.min(Math.max(constraints.minW, requestedW), bound))
  const x = growsLeft ? item.x + item.w - w : item.x
  return { x, w }
}

/** Apply widget size constraints from WIDGET_DEFAULT_SIZES */
export function applyConstraints(item: GridItem, c: SizeConstraints): GridItem {
  let { w, h } = item
  w = Math.max(c.minW, c.maxW !== undefined ? Math.min(w, c.maxW) : w)
  h = Math.max(c.minH, c.maxH !== undefined ? Math.min(h, c.maxH) : h)
  return { ...item, w, h }
}

/** Check if two grid items overlap */
export function isOverlapping(a: GridItem, b: GridItem): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
}

export interface FusionBleed {
  left: number
  right: number
  top: number
  bottom: number
}

/**
 * Visible gap left between neighbouring widgets after fusion. 0 = borders shared. The shell
 * design (raised card, Figma "Shell") shows every widget as its own framed tile with the full
 * 8px grid gutter between them, so this equals the grid margin: no bleed, no fusion.
 */
export const FUSION_GAP_PX = 8

/**
 * How far a widget grows past its grid rect so the dashboard closes up to a `gap`
 * between neighbours: every side takes half the margin minus half that gap, except
 * sides on the canvas edge (left, top, right), which stay flush. `gap` defaults to the
 * full margin — no bleed, so the visible gap is the grid gap itself (8 screen px, see
 * getDashboardGridMetrics).
 *
 * With no gap, right/bottom take one border width more so two neighbours' borders
 * land on the same device pixel instead of doubling up. `hairline` is that border
 * width as rendered, in the grid's own px (see renderedHairlinePx). A flat 1px is
 * only right at 1x with no zoom; anything else leaves the two lines a device pixel
 * apart, which shows as a jog wherever a seam ends at a T-junction.
 */
export function computeFusionBleed(
  item: GridItem,
  cols: number,
  margin: [number, number],
  hairline = 1,
  gap = Math.min(margin[0], margin[1])
): FusionBleed {
  const overlap = gap === 0 ? hairline : 0
  const halfX = Math.max(0, margin[0] / 2 - gap / 2)
  const halfY = Math.max(0, margin[1] / 2 - gap / 2)
  return {
    left: item.x > 0 ? halfX : 0,
    right: item.x + item.w < cols ? halfX + overlap : 0,
    top: item.y > 0 ? halfY : 0,
    bottom: halfY + overlap,
  }
}

/** How far a resize zone reaches into the widget, measured from its visible border. */
export const RESIZE_GRAB_INSIDE = 4
/** How far past the visible border a resize zone reaches on an edge with no neighbour. */
export const RESIZE_GRAB_OUTSIDE = 6
/**
 * How far a bottom zone crosses the seam onto the widget below. Widgets have no top
 * handle, so nothing competes for that side and the line sits mid-target.
 */
export const RESIZE_GRAB_PAST_SEAM = 3

export interface ResizeReach {
  left: number
  right: number
  bottom: number
}

/**
 * How far each resize zone reaches past the widget's grid rect. The visible border
 * sits `bleed` outside that rect (see computeFusionBleed), so a zone confined to the
 * rect makes you aim half a gap inside the line. Where a neighbour shares the edge the
 * zone stops at the seam, so each side of the line belongs to the widget drawn there
 * (a bottom edge goes a little further, as nothing resizes from the top); an open edge
 * reaches a little past the border instead.
 */
export function computeResizeReach(
  item: GridItem,
  layout: GridItem[],
  margin: [number, number],
  bleed: FusionBleed
): ResizeReach {
  const others = layout.filter((o) => o.i !== item.i)
  const overlapsRows = (o: GridItem) => o.y < item.y + item.h && o.y + o.h > item.y
  const overlapsCols = (o: GridItem) => o.x < item.x + item.w && o.x + o.w > item.x
  const hasLeft = others.some((o) => o.x + o.w === item.x && overlapsRows(o))
  const hasRight = others.some((o) => o.x === item.x + item.w && overlapsRows(o))
  const hasBelow = others.some((o) => o.y === item.y + item.h && overlapsCols(o))
  return {
    left: hasLeft ? margin[0] / 2 : bleed.left + RESIZE_GRAB_OUTSIDE,
    right: hasRight ? margin[0] / 2 : bleed.right + RESIZE_GRAB_OUTSIDE,
    bottom: hasBelow ? margin[1] / 2 + RESIZE_GRAB_PAST_SEAM : bleed.bottom + RESIZE_GRAB_OUTSIDE,
  }
}

/**
 * Width a 1px border actually paints at, in CSS px inside an element scaled by `zoom`:
 * the browser floors a border to whole device pixels, never below one.
 */
export function renderedHairlinePx(devicePixelRatio: number, zoom: number): number {
  const devicePx = devicePixelRatio * zoom
  if (!(devicePx > 0)) return 1
  return Math.max(1, Math.floor(devicePx)) / devicePx
}

export type StretchDirection = 'left' | 'right' | 'bottom' | 'bottom-left' | 'bottom-right'

/**
 * Expand an item into the largest empty rectangle available from one of its
 * resize handles. Unlike a regular resize, stretch never reflows neighbours:
 * another widget is a hard boundary, as are the grid and the item's max size.
 */
export function stretchGridItem(
  item: GridItem,
  layout: GridItem[],
  direction: StretchDirection,
  cols: number,
  rows: number,
  constraints: SizeConstraints
): GridItem {
  const growsLeft = direction === 'left' || direction === 'bottom-left'
  const maxWidthByGrid = growsLeft ? item.x + item.w : cols - item.x
  const maxWidth = Math.max(item.w, Math.min(maxWidthByGrid, constraints.maxW ?? maxWidthByGrid))
  const maxHeightByGrid = Math.max(item.h, rows - item.y)
  const maxHeight = Math.max(item.h, Math.min(maxHeightByGrid, constraints.maxH ?? maxHeightByGrid))
  const right = item.x + item.w
  const collides = (candidate: GridItem) =>
    layout.some((other) => other.i !== item.i && isOverlapping(candidate, other))

  let best = item
  let bestArea = item.w * item.h
  let bestGrowth = 0

  const lastWidth = direction === 'bottom' ? item.w : maxWidth
  const lastHeight = direction === 'left' || direction === 'right' ? item.h : maxHeight

  for (let w = item.w; w <= lastWidth; w++) {
    const x = growsLeft ? right - w : item.x
    for (let h = item.h; h <= lastHeight; h++) {
      const candidate = { ...item, x, w, h }
      if (collides(candidate)) continue

      const area = w * h
      const growth = w - item.w + (h - item.h)
      if (area > bestArea || (area === bestArea && growth > bestGrowth)) {
        best = candidate
        bestArea = area
        bestGrowth = growth
      }
    }
  }

  return best
}

/** Map of widget id → its rank in some prior (reference) layout, lowest rank packed first. */
export type ReferenceOrder = Record<string, number>

/**
 * Snapshot a layout's (y, x) order into a stable id → rank map.
 *
 * Feeding this into compactLayout/normalizeLayout anchors the greedy packer's
 * processing order to a layout from BEFORE an interaction (e.g. the last
 * committed layout), instead of whatever y values reflowLayout happened to
 * leave items at mid-interaction. Without this, compaction's sort key is
 * contaminated by reflow's arbitrary push-downs, which can settle into a
 * taller-than-necessary stable arrangement that never shrinks back — see
 * compactLayout's referenceOrder param for how it's consumed.
 */
export function buildReferenceOrder(layout: GridItem[]): ReferenceOrder {
  const sorted = [...layout].sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x))
  const order: ReferenceOrder = {}
  sorted.forEach((item, index) => {
    order[item.i] = index
  })
  return order
}

/** Lexicographically comparable sort key: ranked items always sort before unranked ones. */
function sortKey(
  item: GridItem,
  referenceOrder?: ReferenceOrder
): [number, number, number, string] {
  const rank = referenceOrder?.[item.i]
  return [rank ?? Number.MAX_SAFE_INTEGER, item.y, item.x, item.i]
}

function compareSortKeys(a: GridItem, b: GridItem, referenceOrder?: ReferenceOrder): number {
  const ka = sortKey(a, referenceOrder)
  const kb = sortKey(b, referenceOrder)
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] !== kb[i]) return ka[i] < kb[i] ? -1 : 1
  }
  return 0
}

/**
 * Resolve overlaps vertically WITHOUT floating items up to fill gaps: each item
 * starts the search for a free y at its OWN current y, never at 0. An item that
 * isn't overlapping anything keeps its exact position — a widget removed or moved
 * elsewhere leaves a gap that stays empty instead of pulling everything below it
 * upward. An item that does overlap another is pushed down just enough to clear it.
 *
 * Processing order is the sort key (referenceOrder rank, then y, x, id) — pass a
 * referenceOrder snapshotted from BEFORE the interaction (see buildReferenceOrder) so
 * repeated drags/resizes resolve overlaps in a stable, predictable order rather than
 * one contaminated by reflowLayout's incidental y pushes. Omitting referenceOrder
 * falls back to sorting by current (y, x, id).
 */
export function compactLayout(layout: GridItem[], referenceOrder?: ReferenceOrder): GridItem[] {
  const sorted = [...layout].sort((a, b) => compareSortKeys(a, b, referenceOrder))
  const placed: GridItem[] = []

  for (const item of sorted) {
    let y = item.y
    while (placed.some((p) => isOverlapping({ ...item, y }, p))) {
      y++
    }
    placed.push({ ...item, y })
  }

  return layout.map((item) => placed.find((p) => p.i === item.i)!)
}

/**
 * The single funnel every layout must pass through before entering state.
 *
 * Guarantees three invariants together, so no caller can satisfy one while
 * silently breaking another:
 *   1. every item respects its size constraints (min/max W/H)
 *   2. every item stays within grid bounds (x/w clamped to cols, y >= 0)
 *   3. no two items overlap
 *
 * Constraints and overlap-resolution are applied TOGETHER: applying a min-size
 * that grows an item can never leave a stale overlap behind, because the
 * compaction pass always runs afterward. Idempotent — feeding an already
 * normalized layout returns an equivalent layout.
 *
 * `fixedId`, when given, keeps that item at its input position through the
 * overlap-resolution pass (compactLayoutAroundFixed instead of plain
 * compactLayout) — used by the drag/resize commit path.
 *
 * Neither pass floats items up to fill gaps (see compactLayout's doc
 * comment): every item keeps its own y unless it overlaps another item, in
 * which case it's pushed down just enough to clear it. A widget removed,
 * resized smaller, or dragged away leaves an empty gap that stays empty
 * instead of pulling everything below it upward.
 */
export function normalizeLayout(
  layout: GridItem[],
  constraintsById: ConstraintsById,
  cols: number,
  rows: number,
  referenceOrder?: ReferenceOrder,
  fixedId?: string
): GridItem[] {
  const constrained = layout.map((item) => {
    const c = constraintsById[item.i]
    const sized = c ? applyConstraints(item, c) : item
    return clampToGrid(sized, cols, rows)
  })
  return fixedId
    ? compactLayoutAroundFixed(constrained, fixedId, referenceOrder)
    : compactLayout(constrained, referenceOrder)
}

/**
 * Resolve overlaps around a fixed (dragging) widget.
 * The fixed widget stays at its current position; every other item keeps its own
 * position too unless it overlaps something (see compactLayoutAroundMultipleFixed).
 * Used during drag so the dragging widget isn't moved away from its snapped position.
 */
export function compactLayoutAroundFixed(
  layout: GridItem[],
  fixedId: string,
  referenceOrder?: ReferenceOrder
): GridItem[] {
  return compactLayoutAroundMultipleFixed(layout, [fixedId], referenceOrder)
}

/**
 * Resolve overlaps around multiple fixed widgets (e.g. dragging + swap target).
 * Fixed widgets stay at their current positions; every other item keeps its own
 * position too unless it overlaps something, in which case it's pushed down just
 * enough to clear it — see compactLayout's doc comment for why this doesn't float.
 */
export function compactLayoutAroundMultipleFixed(
  layout: GridItem[],
  fixedIds: string[],
  referenceOrder?: ReferenceOrder
): GridItem[] {
  const fixedSet = new Set(fixedIds)
  const fixed = layout.filter((i) => fixedSet.has(i.i))
  if (fixed.length === 0) return compactLayout(layout, referenceOrder)

  const sorted = [...layout]
    .filter((i) => !fixedSet.has(i.i))
    .sort((a, b) => compareSortKeys(a, b, referenceOrder))

  const placed: GridItem[] = [...fixed]

  for (const item of sorted) {
    let y = item.y
    while (placed.some((p) => isOverlapping({ ...item, y }, p))) {
      y++
    }
    placed.push({ ...item, y })
  }

  return layout.map((item) => placed.find((p) => p.i === item.i)!)
}

/**
 * Find the swap target for a dragging widget.
 *
 * Always returns the IMMEDIATELY ADJACENT widget in the drag direction — the closest
 * one by y-position that has x overlap — rather than the widget with the best area
 * ratio. This prevents the dragging widget from "skipping over" a large adjacent
 * widget to swap with a smaller one further away.
 *
 * The swap is only triggered once the dragging widget has entered the adjacent row
 * (snapped.y >= adjacent.y when dragging down; snapped.y+h <= adjacent.y+h when up).
 */
export function findSwapCollider(
  snapped: GridItem,
  startItem: GridItem,
  layout: GridItem[],
  draggingDown: boolean,
  excludeId: string
): GridItem | null {
  // Step 1: find the y boundary of the immediately adjacent row WITH x overlap.
  let adjacentBoundary: number | null = null

  for (const other of layout) {
    if (other.i === excludeId) continue
    const ox = Math.min(snapped.x + snapped.w, other.x + other.w) - Math.max(snapped.x, other.x)
    if (ox <= 0) continue

    if (draggingDown) {
      if (other.y < startItem.y + startItem.h) continue
      if (adjacentBoundary === null || other.y < adjacentBoundary) adjacentBoundary = other.y
    } else {
      if (other.y + other.h > startItem.y) continue
      if (adjacentBoundary === null || other.y + other.h > adjacentBoundary)
        adjacentBoundary = other.y + other.h
    }
  }

  if (adjacentBoundary === null) return null

  // Step 2: only trigger if snapped has entered the adjacent row.
  const entered = draggingDown
    ? snapped.y >= adjacentBoundary
    : snapped.y + snapped.h <= adjacentBoundary

  if (!entered) return null

  // Step 3: among widgets in that adjacent row, pick the one with most x overlap.
  let best: GridItem | null = null
  let bestX = 0

  for (const other of layout) {
    if (other.i === excludeId) continue
    const isAdjacent = draggingDown
      ? other.y === adjacentBoundary
      : other.y + other.h === adjacentBoundary
    if (!isAdjacent) continue

    const ox = Math.min(snapped.x + snapped.w, other.x + other.w) - Math.max(snapped.x, other.x)
    if (ox > bestX) {
      bestX = ox
      best = other
    }
  }

  return best
}

export type SwapDirection = 'up' | 'down' | 'left' | 'right'
export type DragAxis = 'horizontal' | 'vertical'

export interface DirectionalSwapResult {
  moving: GridItem
  collider: GridItem
}

export function getDominantDragAxis(dx: number, dy: number): DragAxis {
  return Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
}

function satisfiesSizeConstraints(item: GridItem, constraints?: SizeConstraints): boolean {
  if (!constraints) return true
  if (item.w < constraints.minW || item.h < constraints.minH) return false
  if (constraints.maxW !== undefined && item.w > constraints.maxW) return false
  if (constraints.maxH !== undefined && item.h > constraints.maxH) return false
  return true
}

function isWithinGrid(item: GridItem, cols: number, rows: number): boolean {
  return (
    item.x >= 0 &&
    item.y >= 0 &&
    item.w >= 1 &&
    item.h >= 1 &&
    item.x + item.w <= cols &&
    item.y + item.h <= rows
  )
}

/**
 * Find the immediately-adjacent widget on the horizontal axis.
 *
 * This is the horizontal counterpart to findSwapCollider: it only considers
 * the closest x-band in the drag direction that overlaps the moving widget's
 * y-band, then picks the item in that band with the largest y overlap.
 */
export function findHorizontalSwapCollider(
  snapped: GridItem,
  currentItem: GridItem,
  layout: GridItem[],
  draggingRight: boolean,
  excludeId: string
): GridItem | null {
  let adjacentBoundary: number | null = null

  for (const other of layout) {
    if (other.i === excludeId) continue
    const oy = Math.min(snapped.y + snapped.h, other.y + other.h) - Math.max(snapped.y, other.y)
    if (oy <= 0) continue

    if (draggingRight) {
      if (other.x < currentItem.x + currentItem.w) continue
      if (adjacentBoundary === null || other.x < adjacentBoundary) adjacentBoundary = other.x
    } else {
      const otherRight = other.x + other.w
      if (otherRight > currentItem.x) continue
      if (adjacentBoundary === null || otherRight > adjacentBoundary) adjacentBoundary = otherRight
    }
  }

  if (adjacentBoundary === null) return null

  const entered = draggingRight
    ? snapped.x + snapped.w > adjacentBoundary
    : snapped.x < adjacentBoundary
  if (!entered) return null

  let best: GridItem | null = null
  let bestY = 0

  for (const other of layout) {
    if (other.i === excludeId) continue
    const isAdjacent = draggingRight
      ? other.x === adjacentBoundary
      : other.x + other.w === adjacentBoundary
    if (!isAdjacent) continue

    const oy = Math.min(snapped.y + snapped.h, other.y + other.h) - Math.max(snapped.y, other.y)
    if (oy > bestY) {
      bestY = oy
      best = other
    }
  }

  return best
}

/**
 * Build a size-eligible adjacent swap. Returns null when the interaction should
 * fall back to collision reflow instead.
 */
export function buildSizeEligibleSwap(
  snapped: GridItem,
  currentItem: GridItem,
  collider: GridItem,
  direction: SwapDirection,
  cols: number,
  rows: number,
  constraintsById: ConstraintsById = {}
): DirectionalSwapResult | null {
  let moving: GridItem
  let movedCollider: GridItem

  if (direction === 'left' || direction === 'right') {
    if (Math.abs(currentItem.h - collider.h) > 1) return null

    const compactH = Math.min(currentItem.h, collider.h)
    const movingConstraints = constraintsById[currentItem.i]
    const colliderConstraints = constraintsById[collider.i]
    if (
      compactH < (movingConstraints?.minH ?? 1) ||
      compactH < (colliderConstraints?.minH ?? 1) ||
      (movingConstraints?.maxH !== undefined && compactH > movingConstraints.maxH) ||
      (colliderConstraints?.maxH !== undefined && compactH > colliderConstraints.maxH)
    ) {
      return null
    }

    const movingX = direction === 'right' ? currentItem.x + collider.w : collider.x
    const colliderX = direction === 'right' ? currentItem.x : collider.x + currentItem.w

    moving = { ...snapped, x: movingX, y: currentItem.y, h: compactH }
    movedCollider = { ...collider, x: colliderX, y: currentItem.y, h: compactH }
  } else {
    if (currentItem.w !== collider.w) return null

    if (direction === 'up') {
      // collider sits directly above currentItem — currentItem takes its old
      // top slot, collider shifts down by currentItem's height (each keeps
      // its own height; only stacking order swaps).
      moving = { ...snapped, x: currentItem.x, y: collider.y }
      movedCollider = { ...collider, x: currentItem.x, y: collider.y + currentItem.h }
    } else {
      // collider sits directly below currentItem — mirror of the 'up' case:
      // collider takes currentItem's old top slot, currentItem shifts down
      // by collider's height.
      movedCollider = { ...collider, x: currentItem.x, y: currentItem.y }
      moving = { ...snapped, x: currentItem.x, y: currentItem.y + collider.h }
    }
  }

  if (!isWithinGrid(moving, cols, rows) || !isWithinGrid(movedCollider, cols, rows)) return null
  if (isOverlapping(moving, movedCollider)) return null
  if (!satisfiesSizeConstraints(moving, constraintsById[currentItem.i])) return null
  if (!satisfiesSizeConstraints(movedCollider, constraintsById[collider.i])) return null

  return { moving, collider: movedCollider }
}

/**
 * Find the widget most directly in the path of item.
 * Uses overlap / min(item area, other area) so large widgets don't dominate:
 * a small widget 80% covered scores higher than a large widget barely clipped.
 * Returns null if no collision exists.
 */
export function findPrimaryCollision(
  item: GridItem,
  layout: GridItem[],
  excludeId: string
): GridItem | null {
  let best: GridItem | null = null
  let bestRatio = 0
  const itemArea = item.w * item.h
  for (const other of layout) {
    if (other.i === excludeId) continue
    if (!isOverlapping(item, other)) continue
    const ox = Math.min(item.x + item.w, other.x + other.w) - Math.max(item.x, other.x)
    const oy = Math.min(item.y + item.h, other.y + other.h) - Math.max(item.y, other.y)
    const ratio = (ox * oy) / Math.min(itemArea, other.w * other.h)
    if (ratio > bestRatio) {
      bestRatio = ratio
      best = other
    }
  }
  return best
}

/**
 * Reflow: push items that collide with the moving item downward.
 * The moving item is treated as fixed (highest priority).
 * Items are processed top-to-bottom; each is pushed just enough to clear
 * the moving item and all previously processed items. Clamped to grid rows.
 */
export function reflowLayout(layout: GridItem[], movingId: string, rows = 24): GridItem[] {
  const result = layout.map((item) => ({ ...item }))
  const moving = result.find((i) => i.i === movingId)
  if (!moving) return result

  const others = result.filter((i) => i.i !== movingId)
  // Process top-to-bottom so each item only needs to check items above it
  others.sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x))

  for (let i = 0; i < others.length; i++) {
    const item = others[i]
    let minY = item.y
    let changed = true

    // Keep iterating until this item clears all obstacles (converges in O(n) per item)
    while (changed) {
      changed = false

      if (isOverlapping({ ...item, y: minY }, moving)) {
        const needed = moving.y + moving.h
        if (needed > minY) {
          minY = needed
          changed = true
        }
      }

      for (let j = 0; j < i; j++) {
        const prev = others[j]
        if (isOverlapping({ ...item, y: minY }, prev)) {
          const needed = prev.y + prev.h
          if (needed > minY) {
            minY = needed
            changed = true
          }
        }
      }
    }

    // Clamp to grid boundary only when it doesn't re-create overlap.
    // If clamping would overlap the moving item or a previously-placed
    // item, allow the item to overflow rows instead — compactLayoutAroundFixed
    // (drag path) or normalizeLayout's pass (commit path) will still push it
    // down just enough to clear the conflict, without floating anything up.
    const maxY = Math.max(0, rows - item.h)
    if (minY <= maxY) {
      item.y = minY
    } else {
      const clampedY = maxY
      const overlapMoving = isOverlapping({ ...item, y: clampedY }, moving)
      const overlapPrev = others.slice(0, i).some((p) => isOverlapping({ ...item, y: clampedY }, p))
      item.y = overlapMoving || overlapPrev ? minY : clampedY
    }
  }

  return result
}

/**
 * Place a dragged item at its current snapped position without carrying any
 * collision response forward from earlier points in the pointer path.
 *
 * Pointer movement can cross several unrelated widgets before reaching an
 * empty drop target. Recomputing from the drag-start layout ensures those
 * transient collisions cannot turn into persistent swaps at drop time.
 */
export function placeDraggedItem(
  startLayout: GridItem[],
  snapped: GridItem,
  rows = 24,
  referenceOrder?: ReferenceOrder
): GridItem[] {
  const updated = startLayout.map((item) => (item.i === snapped.i ? snapped : item))
  const reflowed = reflowLayout(updated, snapped.i, rows)
  return compactLayoutAroundFixed(reflowed, snapped.i, referenceOrder)
}

/**
 * Reflow: push items that collide with the moving/resizing item sideways.
 * The moving item is treated as fixed. Items are processed in push order
 * (left-to-right for a rightward push, right-to-left for a leftward push) so
 * each only needs to check previously processed items. Clamped to grid
 * columns — unlike reflowLayout's row axis, there's no "overflow and let
 * compaction sort it out later" option here, so a push that has nowhere left
 * to go clamps at the boundary (may leave residual overlap in a fully packed
 * row, same as reflowLayout's own boundary-clamp fallback).
 */
export function reflowLayoutHorizontal(
  layout: GridItem[],
  movingId: string,
  cols: number,
  direction: 'left' | 'right'
): GridItem[] {
  const result = layout.map((item) => ({ ...item }))
  const moving = result.find((i) => i.i === movingId)
  if (!moving) return result

  const others = result.filter((i) => i.i !== movingId)
  others.sort((a, b) => (direction === 'right' ? a.x - b.x : b.x - a.x))

  for (let i = 0; i < others.length; i++) {
    const item = others[i]
    let x = item.x
    let changed = true

    while (changed) {
      changed = false

      if (isOverlapping({ ...item, x }, moving)) {
        const needed = direction === 'right' ? moving.x + moving.w : moving.x - item.w
        if (direction === 'right' ? needed > x : needed < x) {
          x = needed
          changed = true
        }
      }

      for (let j = 0; j < i; j++) {
        const prev = others[j]
        if (isOverlapping({ ...item, x }, prev)) {
          const needed = direction === 'right' ? prev.x + prev.w : prev.x - item.w
          if (direction === 'right' ? needed > x : needed < x) {
            x = needed
            changed = true
          }
        }
      }
    }

    item.x = direction === 'right' ? Math.min(x, cols - item.w) : Math.max(0, x)
  }

  return result
}

/**
 * Move `item` by exactly one grid unit in `direction`, for keyboard-driven
 * repositioning (arrow keys). Mirrors the pointer-drag step: try a
 * size-eligible swap with the immediately adjacent widget first, falling
 * back to reflow + compact-around-fixed when no swap qualifies. Returns the
 * input layout unchanged if the move is blocked by a grid edge.
 */
export function stepDirectionalMove(
  item: GridItem,
  layout: GridItem[],
  direction: SwapDirection,
  cols: number,
  rows: number,
  constraintsById: ConstraintsById = {},
  referenceOrder?: ReferenceOrder
): GridItem[] {
  const dx = direction === 'left' ? -1 : direction === 'right' ? 1 : 0
  const dy = direction === 'up' ? -1 : direction === 'down' ? 1 : 0
  const moved = clampToGrid({ ...item, x: item.x + dx, y: item.y + dy }, cols, rows)
  if (moved.x === item.x && moved.y === item.y) return layout

  const collider =
    dx !== 0
      ? findHorizontalSwapCollider(moved, item, layout, dx > 0, item.i)
      : findSwapCollider(moved, item, layout, dy > 0, item.i)

  if (collider) {
    const swap = buildSizeEligibleSwap(
      moved,
      item,
      collider,
      direction,
      cols,
      rows,
      constraintsById
    )
    if (swap) {
      const swapped = layout.map((g) => {
        if (g.i === item.i) return swap.moving
        if (g.i === collider.i) return swap.collider
        return g
      })
      return compactLayoutAroundMultipleFixed(
        swapped,
        [swap.moving.i, swap.collider.i],
        referenceOrder
      )
    }
  }

  const updated = layout.map((g) => (g.i === item.i ? moved : g))
  const reflowed = reflowLayout(updated, item.i, rows)
  return compactLayoutAroundFixed(reflowed, item.i, referenceOrder)
}

/**
 * Move `item` to the top of the grid (y = 0), reflowing every other widget
 * downward to clear it and compacting the rest back up around it — same
 * reflow-then-compact-around-fixed shape as the drag/stepDirectionalMove path,
 * just without a swap step since there's no single "adjacent" target. Returns
 * the input layout unchanged if the item is already at the top.
 */
export function moveItemToTop(
  item: GridItem,
  layout: GridItem[],
  rows: number,
  referenceOrder?: ReferenceOrder
): GridItem[] {
  if (item.y === 0) return layout

  const moved = { ...item, y: 0 }
  const updated = layout.map((g) => (g.i === item.i ? moved : g))
  const reflowed = reflowLayout(updated, item.i, rows)
  return compactLayoutAroundFixed(reflowed, item.i, referenceOrder)
}
